import { selectsCrawlerPreferences } from "./constants";

import type { IntegrationScope } from "./constants";
import type {
  CrawlerPreferenceData,
  CrawlerPreferenceType,
  GetCrawlerPreferenceResponse,
} from "./types/crawlerPreference";

/**
 * Centralized integration copy and configuration
 * Ensures consistency across all integration utilities
 */
const INTEGRATIONS_COPY: Record<
  string,
  {
    description: string;
    authDescription: string;
    restrictionText: string;
    crawlerPreferenceNoun?: string;
    crawlerPreferenceLoadingHeading?: string;
    crawlerPreferenceLoadingDescription?: string;
    limitationNote?: string;
  }
> = {
  // Email Apps
  gmail: {
    description: "Search your emails and get answers from your inbox",
    authDescription:
      "Connect your Gmail account to search through your emails and get instant answers from your inbox",
    restrictionText: "email",
  },
  outlook: {
    description: "Find emails instantly and ask questions about conversations",
    authDescription:
      "Connect your Outlook account to find emails instantly and ask questions about your conversations",
    restrictionText: "email",
  },

  // Drive/Storage Apps
  box: {
    description: "Search your files and ask questions about documents",
    authDescription:
      "Connect your Box account to search through your files and get insights from your stored documents",
    restrictionText: "files",
    crawlerPreferenceNoun: "Folders",
  },
  onedrive: {
    description: "Access your files and get insights from stored content",
    authDescription:
      "Connect your OneDrive account to access your files and get answers from your stored content",
    restrictionText: "files",
    crawlerPreferenceNoun: "Folders",
    limitationNote:
      "Only folders in your own OneDrive can be synced. Files others have shared with you won't appear. To include one, save a copy to your OneDrive or upload it directly.",
  },
  sharepoint: {
    description: "Search team sites and ask questions about shared files",
    authDescription:
      "Connect your SharePoint account to search team sites and ask questions about shared files",
    restrictionText: "files",
    crawlerPreferenceNoun: "Sites and Subsites",
  },
  googledrive: {
    description: "Find any file and get answers from your documents",
    authDescription:
      "Connect your Google Drive account to find any file and get answers from your documents",
    restrictionText: "files",
    crawlerPreferenceNoun: "Folders",
  },
  dropbox: {
    description: "Search through files and ask questions about your content",
    authDescription:
      "Connect your Dropbox account to search through files and ask questions about your content",
    restrictionText: "files",
    crawlerPreferenceNoun: "Folders",
  },
  azureblob: {
    description: "Access cloud data and get insights from stored files",
    authDescription:
      "Connect your Azure Blob Storage to access cloud data and get insights from stored files",
    restrictionText: "files",
  },
  azure_storage: {
    description: "Access cloud data and get insights from stored files",
    authDescription:
      "Connect your Azure Storage to access cloud data and get insights from stored files",
    restrictionText: "files",
    crawlerPreferenceNoun: "Containers",
  },

  // Notes Apps
  onenote: {
    description: "Search your notebooks and ask questions about notes",
    authDescription:
      "Connect your OneNote account to search through your notebooks and ask questions about your notes",
    restrictionText: "notes",
    crawlerPreferenceNoun: "Notebooks",
  },
  evernote: {
    description: "Search your notebooks and ask questions about notes",
    authDescription:
      "Connect your Evernote account to search through your notes and get insights from your content",
    restrictionText: "notes",
    crawlerPreferenceNoun: "Notebooks",
  },

  // Communication Apps
  teams: {
    description: "Search chats and meeting recordings for insights",
    authDescription:
      "Connect your Microsoft Teams account to search through chats and meeting recordings for insights",
    restrictionText: "chats or recordings",
    crawlerPreferenceNoun: "Chats",
  },
  slack: {
    description: "Find conversations and get answers from chat history",
    authDescription:
      "Connect your Slack account to find conversations and get answers from your chat history",
    restrictionText: "messages",
    crawlerPreferenceNoun: "Chats",
  },
  telegram: {
    description: "Search your messages and get insights from conversations",
    authDescription:
      "Connect your Telegram account to search through your messages and get insights from conversations",
    restrictionText: "messages",
    crawlerPreferenceNoun: "Contacts",
    crawlerPreferenceLoadingHeading: "Setting up your contacts...",
    crawlerPreferenceLoadingDescription:
      "You'll get an email when it's time to choose which ones to sync.",
  },
  whatsapp: {
    description: "Search your messages and get insights from conversations",
    authDescription:
      "Connect your WhatsApp account to search through your messages and get insights from conversations",
    restrictionText: "messages",
  },

  // Social Media
  twitter: {
    description: "Search your tweets and analyze social activity",
    authDescription:
      "Connect your Twitter account to search through your tweets and analyze your social activity",
    restrictionText: "tweets",
  },
};

/**
 * Gets the description for an integration
 * @param sourceId - The integration source id
 * @returns The appropriate description for the integration
 */
export function getIntegrationDescription(sourceId: string): string {
  const integration = INTEGRATIONS_COPY[sourceId];
  if (integration) {
    return integration.description;
  }

  return `Connect your ${sourceId} account`;
}

function getCrawlerPreferenceStepName(sourceId: string): string {
  const noun = INTEGRATIONS_COPY[sourceId]?.crawlerPreferenceNoun;

  return noun ? `Choose ${noun}` : "Crawler Preferences";
}

/**
 * What a source's crawler preferences are made of — folders, chats, notebooks.
 * @param sourceId - The integration source id
 * @returns The lowercased plural noun, or a neutral fallback
 */
export function getCrawlerPreferenceNoun(sourceId: string): string {
  return (
    INTEGRATIONS_COPY[sourceId]?.crawlerPreferenceNoun?.toLowerCase() ??
    "content"
  );
}

/**
 * Tooltip for the refresh action, which is labelled just "Refresh" everywhere.
 * @param sourceId - The integration source id
 * @returns Tooltip copy for the refresh action
 */
export function getCrawlerPreferenceRefreshTooltip(sourceId: string): string {
  return `Refresh ${getCrawlerPreferenceNoun(sourceId)}`;
}

export function getIntegrationLimitationNote(
  sourceId: string,
): string | undefined {
  return INTEGRATIONS_COPY[sourceId]?.limitationNote;
}

export function getCrawlerPreferenceLoadingText(sourceId: string) {
  const integration = INTEGRATIONS_COPY[sourceId];

  return {
    heading:
      integration?.crawlerPreferenceLoadingHeading ||
      "We're retrieving your crawler preferences. This may take few minutes, please check back in 10min.",
    description:
      integration?.crawlerPreferenceLoadingDescription ||
      "Once preferences are loaded, you'll be able to choose content to sync.",
  };
}

/**
 * Gets the authentication description for an integration
 * @param sourceId - The integration source id
 * @returns The appropriate description for the authentication step
 */
export function getAuthenticationDescription(sourceId: string): string {
  const integration = INTEGRATIONS_COPY[sourceId];
  if (integration) {
    return integration.authDescription;
  }

  return `Connect your ${sourceId} account to access and search through your content`;
}

/**
 * Gets the dynamic value for the "will not be able to write or modify" text
 * @param sourceId - The integration source id
 * @returns The appropriate dynamic value (email, files, chats, etc.)
 */
export function getAuthenticationRestrictionText(sourceId: string): string {
  const integration = INTEGRATIONS_COPY[sourceId];
  if (integration) {
    return integration.restrictionText;
  }

  return "content";
}

/**
 * Gets the step configuration for an integration
 * @param sourceId - The integration label/source name
 * @returns Array of step configurations
 */
export function getIntegrationSteps(
  sourceId: string,
  integrationScope?: IntegrationScope,
) {
  const hasPreferences = selectsCrawlerPreferences(sourceId, integrationScope);

  if (hasPreferences) {
    // 3 steps: Authenticate → Dynamic Crawler Preference → Finish
    return [
      { key: "authenticate" as const, label: "Authenticate", number: 1 },
      {
        key: "crawler_preference" as const,
        label: getCrawlerPreferenceStepName(sourceId),
        number: 2,
      },
      { key: "result" as const, label: "Finish", number: 3 },
    ] as const;
  } else {
    // 2 steps: Authenticate → Finish
    return [
      { key: "authenticate" as const, label: "Authenticate", number: 1 },
      { key: "result" as const, label: "Finish", number: 2 },
    ] as const;
  }
}

/**
 * Where an account's crawler preference read currently stands.
 *
 * `expired` outranks everything: folders fetched earlier are still stored so the
 * read succeeds, but nothing can be refreshed until the account is reconnected.
 */
export type CrawlerPreferenceStatus =
  | "expired"
  | "error"
  | "loading"
  | "fetching"
  | "ready";

/**
 * Whether a crawler-preference read has landed an actual tree, as opposed to a
 * WAIT/ERROR envelope. Narrows the response union for callers that need the
 * preferences themselves.
 *
 * @param data - The crawler-preference query's data
 * @returns True when the tree is present
 */
export function isCrawlerPreferenceReady(
  data?: GetCrawlerPreferenceResponse["results"],
): data is CrawlerPreferenceData {
  return !!data && "preferences" in data && Array.isArray(data.preferences);
}

/**
 * Reads a crawler-preference query into a single status.
 *
 * A trigger only queues the crawl, so progress has to come from the read: the
 * query lands on WAIT (`fetching`) and polls itself until the tree arrives.
 * `isTriggering` folds the queued POST into `fetching` so the status does not
 * fall back to `ready` between the trigger returning and the refetch landing.
 *
 * @param input - The query's data, loading/error flags, the account's token
 *   validity, and whether a trigger is in flight
 * @returns The status and the server's error message when there is one
 */
export function deriveCrawlerPreferenceStatus({
  data,
  isLoading,
  isError,
  isValidToken,
  isTriggering,
}: {
  data?: GetCrawlerPreferenceResponse["results"];
  isLoading?: boolean;
  isError?: boolean;
  isValidToken: boolean;
  isTriggering?: boolean;
}): { status: CrawlerPreferenceStatus; errorMessage?: string } {
  if (!isValidToken) return { status: "expired" };

  const errorMessage =
    data && "error_message" in data ? data.error_message : undefined;

  if (isError || (data && "process" in data && data.process === "ERROR")) {
    return { status: "error", errorMessage };
  }
  if (isTriggering) return { status: "fetching" };
  if (isLoading) return { status: "loading" };
  return { status: isCrawlerPreferenceReady(data) ? "ready" : "fetching" };
}

export const SITE_DRIVE_TYPE = "site";
const SITE_LIST_TYPE = "Sites";

export const isSiteGroupList = (groups: CrawlerPreferenceType[]) =>
  groups.length > 0 &&
  groups.every((group) => group.drive_type === SITE_DRIVE_TYPE);

const tickedSiteIds = (groups: CrawlerPreferenceType[]) =>
  new Set(
    groups.flatMap((group) =>
      group.data.filter((row) => row.status).map((row) => row.source_id),
    ),
  );

export const toSiteList = (
  groups: CrawlerPreferenceType[],
): CrawlerPreferenceType[] => {
  if (!isSiteGroupList(groups)) return groups;
  const ticked = tickedSiteIds(groups);
  const sites = groups.flatMap((group) =>
    group.data
      .filter((row) => row.source_id === group.drive_id)
      .map((row) => ({ ...row, status: ticked.has(row.source_id) })),
  );
  return [{ type: SITE_LIST_TYPE, data: sites }];
};

export const fromSiteList = (
  list: CrawlerPreferenceType[],
  groups: CrawlerPreferenceType[],
): CrawlerPreferenceType[] => {
  if (!isSiteGroupList(groups)) return list;
  const before = tickedSiteIds(groups);
  const changed = new Map<string, boolean>(
    (list[0]?.data ?? [])
      .filter((row) => row.status !== before.has(row.source_id))
      .map((row) => [row.source_id, row.status]),
  );
  return groups.map((group) => ({
    ...group,
    data: group.data.map((row) => ({
      ...row,
      status: changed.get(row.source_id) ?? row.status,
    })),
  }));
};
